import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useStaleCache } from '@/lib/cache'
import {
  Activity, Users, Package, Receipt, FileText, BookMarked, Menu, Megaphone, Settings,
  MessageSquare, Database,
  Sun, Moon, LogOut, Languages, ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useI18n, type TKey } from '@/lib/i18n'
import { useTheme } from '@/lib/theme'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/Toast'
import { markSignedOut } from '@/lib/session'
import { api, adminLogout, type MenuItem } from '@/lib/api'
import { FuelioIcon } from '@/components/FuelioIcon'

interface LeafItem {
  to: string
  /** Menu overlay key. Must match the seeded `menus.code` for scope='admin'. */
  code: string
  labelKey: TKey
  Icon: LucideIcon
}
interface GroupItem {
  /** Group header label — UI-only, no route, no DB overlay. */
  labelKey: TKey
  Icon: LucideIcon
  children: LeafItem[]
}
type NavNode = LeafItem | GroupItem

function isGroup(n: NavNode): n is GroupItem {
  return (n as GroupItem).children !== undefined
}

// 分组结构：顶级"首页"单独，其他三组（用户管理 / 商品管理 / 平台管理）。
// DB menus overlay 仍按 leaf code 应用 visible / labelZh / labelEn / sortOrder /
// active，group 标题纯 UI 装饰不参与 overlay。
const navTree: NavNode[] = [
  { to: '/', code: 'stats', labelKey: 'nav.stats', Icon: Activity },

  {
    labelKey: 'nav.group.users', Icon: Users,
    children: [
      { to: '/users', code: 'users', labelKey: 'nav.users', Icon: Users },
    ],
  },

  {
    labelKey: 'nav.group.products', Icon: Package,
    children: [
      { to: '/products', code: 'products', labelKey: 'nav.products', Icon: Package },
      { to: '/orders',   code: 'orders',   labelKey: 'nav.orders',   Icon: Receipt },
    ],
  },

  {
    labelKey: 'nav.group.support', Icon: MessageSquare,
    children: [
      { to: '/chat',    code: 'chat',    labelKey: 'nav.chat',   Icon: MessageSquare },
      { to: '/ai-chat', code: 'ai-chat', labelKey: 'nav.aiChat', Icon: MessageSquare },
    ],
  },

  {
    labelKey: 'nav.group.platform', Icon: Settings,
    children: [
      { to: '/platform-config', code: 'platform-config', labelKey: 'nav.platformConfig', Icon: Settings },
      { to: '/policies',        code: 'policies',        labelKey: 'nav.policies',       Icon: FileText },
      { to: '/dictionaries',    code: 'dictionaries',    labelKey: 'nav.dict',           Icon: BookMarked },
      { to: '/menus',           code: 'menus',           labelKey: 'nav.menus',          Icon: Menu },
      { to: '/announcements',   code: 'announcements',   labelKey: 'nav.announcements',  Icon: Megaphone },
    ],
  },

  // 数据同步 —— 第三方平台 SKU / 库存镜像（zzshu / 未来更多）。
  // 单独成组，方便以后加更多源（每个源一个 leaf）。
  {
    labelKey: 'nav.group.dataSync', Icon: Database,
    children: [
      { to: '/data-sync', code: 'data-sync', labelKey: 'nav.dataSync', Icon: Database },
    ],
  },
]

// 退出分流：取决于当前会话是怎么进来的（whoami.via）。
//   - access：CF Access JWT → /cdn-cgi/access/logout 让 CF 清 CF_Authorization
//     cookie 并把用户送回 IdP，下次进来重新走 SSO。
//   - cookie：普通管理员，密码/验证码 cookie 登录 → POST /auth/admin-logout
//     把 admin cookie 删掉 + refresh jti 黑名单，然后回 /login。
//   - dev-bypass：本地开发模式，没有真会话；标记 markSignedOut 让 Layout 拦
//     住 DEV_ADMIN_BYPASS 自动重入，再回 /login。
const LOGIN_PATH = '/login?signed-out=1'
const ACCESS_LOGOUT_PATH = '/cdn-cgi/access/logout'

export function Sidebar({
  adminEmail,
  adminVia,
  mobileOpen = false,
  onMobileClose,
}: {
  adminEmail?: string
  adminVia?: string
  /** mobile drawer open state — desktop ignores this and is always visible */
  mobileOpen?: boolean
  /** called when user wants to close the drawer (link click / scrim / Esc) */
  onMobileClose?: () => void
}) {
  const { t, lang, setLang } = useI18n()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const [theme, setTheme] = useTheme()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  // 路由切换时自动关闭 mobile drawer：点 NavLink 跳过去后 sidebar 不该
  // 还盖着内容。desktop 上 mobileOpen 永远是 false，这个 effect 等于 no-op。
  useEffect(() => {
    if (mobileOpen) onMobileClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // ESC 关闭 drawer（mobile only）。desktop 上 mobileOpen=false 短路掉。
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onMobileClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen, onMobileClose])

  // Menu overlay: pull /admin/menus and apply visible / labelZh / labelEn /
  // sortOrder / active to the hardcoded items. Failures are silent — the
  // sidebar falls back to the in-code defaults (which is what we want if the
  // user opened an old cached SPA against a fresh API).
  // Stale-while-revalidate：sessionStorage 缓存上次的 overlay，二次进入侧栏
  // 不再先显代码默认 label 再跳成数据库 label。
  const { data: cachedMenus } = useStaleCache(
    'admin.menus.v1',
    () => api.listMenus('admin').then((r) => r.items),
  )
  const menuOverlay = useMemo<Record<string, MenuItem>>(() => {
    if (!cachedMenus) return {}
    const map: Record<string, MenuItem> = {}
    for (const m of cachedMenus) map[m.code] = m
    return map
  }, [cachedMenus])

  // 折叠状态：localStorage 持久化，跨刷新保留。默认全部展开。
  // 点击 group 标题切换。包含当前激活 leaf 的组始终自动展开（避免用户折叠后
  // 再点别的 leaf 导致"看不到当前位置")。
  const COLLAPSE_KEY = 'fuelio.admin.nav.collapsed'
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    if (typeof localStorage === 'undefined') return {}
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}') } catch { return {} }
  })
  function toggleCollapse(key: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      return next
    })
  }
  // 当前 active path 用于"包含 active leaf 的 group 强制展开"
  const activePath = typeof window !== 'undefined' ? window.location.pathname : '/'

  // 解析单个 leaf —— 套上 menus 表的 overlay；返回 null 表示该项被显式隐藏。
  type ResolvedLeaf = { it: LeafItem; label: string; active: boolean; sortOrder: number }
  function resolveLeaf(leaf: LeafItem): ResolvedLeaf | null {
    const ov = menuOverlay[leaf.code]
    if (!ov) return { it: leaf, label: t(leaf.labelKey), active: true, sortOrder: 0 }
    if (ov.visible !== 1) return null
    const label = lang === 'en' ? (ov.labelEn ?? ov.labelZh ?? t(leaf.labelKey)) : ov.labelZh
    return { it: leaf, label, active: ov.active === 1, sortOrder: ov.sortOrder }
  }

  // 整理树：group 内子项按 overlay sortOrder 排序；空 group（所有子项都被隐藏）
  // 整组从渲染中删除避免空标题悬挂。
  type RenderNode =
    | { kind: 'leaf'; leaf: ResolvedLeaf }
    | { kind: 'group'; labelKey: TKey; Icon: LucideIcon; children: ResolvedLeaf[] }
  const visibleNodes: RenderNode[] = useMemo(() => {
    const out: RenderNode[] = []
    for (const n of navTree) {
      if (!isGroup(n)) {
        const r = resolveLeaf(n)
        if (r) out.push({ kind: 'leaf', leaf: r })
      } else {
        const kids = n.children
          .map(resolveLeaf)
          .filter((x): x is ResolvedLeaf => x !== null)
          .sort((a, b) => a.sortOrder - b.sortOrder)
        if (kids.length > 0) {
          out.push({ kind: 'group', labelKey: n.labelKey, Icon: n.Icon, children: kids })
        }
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOverlay, lang, t])

  async function performSignOut() {
    setSigningOut(true)
    markSignedOut()  // dev gate + UX 立刻防抖；harmless in prod
    // cookie 通道：先 POST /auth/admin-logout 让后端删 cookie + 黑名单 refresh
    // jti，再做 toast + 跳转。失败也无所谓 —— cookie 过期会被 middleware 拒。
    if (adminVia === 'cookie') {
      try { await adminLogout() } catch { /* ignore */ }
    }
    toast.success(t('app.signOut.toast.title'))
    // 500ms gives the toast time to show before the route swap.
    setTimeout(() => {
      if (adminVia === 'access') {
        // CF Access：full page nav 让 edge 清 CF_Authorization 并送回 IdP
        window.location.href = ACCESS_LOGOUT_PATH
      } else {
        // cookie / dev-bypass / unknown → 客户端跳 /login
        navigate(LOGIN_PATH)
      }
    }, 500)
  }

  return (
    <>
      {/* mobile-only scrim：drawer 打开时盖住内容 + 点击关闭。md+ 永不显示。 */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-black/55 transition-opacity md:hidden',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onMobileClose}
      />

      <aside
        className={cn(
          'border-r border-border bg-card flex flex-col',
          // desktop：原 w-56 静态布局，参与 flex
          'md:static md:w-56 md:translate-x-0 md:shrink-0',
          // mobile：fixed drawer，从左侧滑入
          'fixed inset-y-0 left-0 z-40 w-64 transition-transform md:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
      <div className="px-4 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <FuelioIcon size={32} className="rounded-md shadow-sm"/>
          <div className="leading-tight">
            <div className="font-semibold tracking-tight">Fuelio</div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{t('app.brand.role')}</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {visibleNodes.map((node, i) => {
          if (node.kind === 'leaf') {
            const { it, label, active } = node.leaf
            const Icon = it.Icon
            if (!active) {
              return (
                <span key={it.to} className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed" title={label}>
                  <Icon size={16} strokeWidth={1.75}/>
                  <span>{label}</span>
                </span>
              )
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === '/'}
                className={({ isActive }) => cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <Icon size={16} strokeWidth={1.75}/>
                <span>{label}</span>
              </NavLink>
            )
          }
          // group：标题可点击折叠子项。包含当前路由的组强制展开。
          const GroupIcon = node.Icon
          const groupKey = 'g-' + node.labelKey
          const containsActive = node.children.some(({ it }) => it.to === activePath)
          const isCollapsed = collapsed[groupKey] && !containsActive
          return (
            <div key={'group-' + i} className="pt-2 first:pt-0">
              <button
                type="button"
                onClick={() => toggleCollapse(groupKey)}
                className="w-full flex items-center gap-2 px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 hover:text-foreground transition-colors"
              >
                <GroupIcon size={12} strokeWidth={2}/>
                <span className="flex-1 text-left">{t(node.labelKey)}</span>
                <ChevronDown
                  size={13}
                  strokeWidth={2}
                  className={cn('transition-transform', isCollapsed ? '-rotate-90' : 'rotate-0')}
                />
              </button>
              {!isCollapsed && (
                <div className="space-y-0.5">
                  {node.children.map(({ it, label, active }) => {
                    const Icon = it.Icon
                    if (!active) {
                      return (
                        <span key={it.to} className="flex items-center gap-2.5 rounded-md pl-7 pr-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed" title={label}>
                          <Icon size={15} strokeWidth={1.75}/>
                          <span>{label}</span>
                        </span>
                      )
                    }
                    return (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        className={({ isActive }) => cn(
                          'flex items-center gap-2.5 rounded-md pl-7 pr-3 py-2 text-sm transition-colors',
                          isActive ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Icon size={15} strokeWidth={1.75}/>
                        <span>{label}</span>
                      </NavLink>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Settings strip — language toggle + theme toggle */}
      <div className="border-t border-border px-2 py-2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
          aria-label="Toggle language"
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <Languages size={14} strokeWidth={1.75}/>
          <span className="font-medium">{t('app.action.toggleLang')}</span>
        </button>
        <button
          type="button"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={t('app.action.toggleTheme')}
          title={t('app.action.toggleTheme')}
          className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {theme === 'dark' ? <Sun size={14} strokeWidth={1.75}/> : <Moon size={14} strokeWidth={1.75}/>}
        </button>
      </div>

      {/* Identity + sign out */}
      <div className="border-t border-border px-3 py-3 text-xs">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate text-foreground/80" title={adminEmail}>
            {adminEmail ?? t('app.loading')}
          </div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-danger transition-colors shrink-0"
            title={t('app.action.signOut')}
          >
            <LogOut size={12} strokeWidth={1.75}/>
            {t('app.action.signOut')}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={t('app.signOut.confirm.title')}
        message={t('app.signOut.confirm.body')}
        confirmLabel={t('app.signOut.confirm.confirm')}
        cancelLabel={t('app.signOut.confirm.cancel')}
        tone="danger"
        busy={signingOut}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={performSignOut}
      />
    </aside>
    </>
  )
}
